import apiService, { SLOW_API_TIMEOUT_MS } from '../api'

const xeroReadConfig = { timeout: SLOW_API_TIMEOUT_MS }

export interface XeroStatus {
  connected: boolean
  tenantId?: string
  tenantName?: string
  expiresAt?: string
  isExpired?: boolean
  tokenValid?: boolean
}

export interface XeroAuthUrlResponse {
  authUrl: string
}

export interface XeroSyncResponse {
  synced: number
  message: string
}

export interface XeroContactsSyncResponse {
  synced: number
  customers: number
  suppliers: number
  message: string
}

export interface XeroSnapshotResponse {
  organisationName: string
  tenantName: string
  contactCounts?: { customers: number; suppliers: number }
  message: string
}

export async function getXeroStatus(): Promise<XeroStatus> {
  const res = await apiService.get<{ success: boolean; data: XeroStatus }>('/xero/status', xeroReadConfig)
  return (res as any)?.data ?? res
}

export async function getXeroAuthUrl(): Promise<string> {
  const res = await apiService.get<{ success: boolean; data: { authUrl: string } }>('/xero/auth-url')
  return (res as any)?.data?.authUrl ?? ''
}

export async function disconnectXero(tenantId?: string): Promise<{ disconnected: boolean }> {
  const url = tenantId ? `/xero/disconnect?tenantId=${encodeURIComponent(tenantId)}` : '/xero/disconnect'
  const res = await apiService.post<{ success: boolean; data: { disconnected: boolean } }>(url)
  return (res as any)?.data ?? { disconnected: true }
}

const XERO_SYNC_TIMEOUT = 300000 // 5 min - sync can paginate through many records

export async function syncXeroInvoices(): Promise<XeroSyncResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroSyncResponse }>('/xero/sync/invoices', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export async function syncXeroPurchases(): Promise<XeroSyncResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroSyncResponse }>('/xero/sync/purchases', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export async function syncXeroPayments(): Promise<XeroSyncResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroSyncResponse }>('/xero/sync/payments', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export async function syncXeroContacts(): Promise<XeroContactsSyncResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroContactsSyncResponse }>('/xero/sync/contacts', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export async function syncXeroProductsServices(): Promise<XeroSyncResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroSyncResponse }>('/xero/sync/products-services', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export interface XeroSyncAllResponse {
  invoices: { ok: boolean; synced?: number; message?: string; error?: string }
  purchases: { ok: boolean; synced?: number; message?: string; error?: string }
  payments: { ok: boolean; synced?: number; message?: string; error?: string }
  contacts: { ok: boolean; synced?: number; message?: string; error?: string; customers?: number; suppliers?: number }
  productsServices: { ok: boolean; synced?: number; message?: string; error?: string }
}

export async function syncXeroAll(): Promise<XeroSyncAllResponse> {
  const res = await apiService.post<{ success: boolean; data: XeroSyncAllResponse }>('/xero/sync/all', undefined, { timeout: XERO_SYNC_TIMEOUT })
  return (res as any)?.data ?? res
}

export async function getXeroBusinessSnapshot(): Promise<XeroSnapshotResponse> {
  const res = await apiService.get<{ success: boolean; data: XeroSnapshotResponse }>('/xero/snapshot', xeroReadConfig)
  return (res as any)?.data ?? res
}

/** One bill/transaction row for an item */
export interface XeroItemTransactionRow {
  invoiceID: string
  invoiceNumber: string
  date: string
  reference: string
  type: string
  quantity: number
  unitPrice: number
  amount: number
  openInXeroUrl: string
}

/** Single Xero item (product/service) detail for in-app popup */
export interface XeroItemDetail {
  item: {
    itemID: string
    code: string
    name: string
    description?: string
    purchaseDescription?: string
    purchaseDetails?: {
      unitPrice?: number
      accountCode?: string
      taxType?: string
    }
    salesDetails?: {
      unitPrice?: number
      accountCode?: string
      taxType?: string
    }
    quantityOnHand?: number
    isTrackedAsInventory?: boolean
    isSold?: boolean
    isPurchased?: boolean
  } | null
  latestTransactions: XeroItemTransactionRow[]
}

export async function getXeroItemDetail(itemId: string): Promise<XeroItemDetail> {
  const res = await apiService.get<{ success: boolean; data: XeroItemDetail }>(`/xero/items/${encodeURIComponent(itemId)}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Fetch bill PDF from Xero by invoice ID; returns a blob URL for in-app viewer. */
export async function getXeroBillPdfUrl(xeroInvoiceId: string): Promise<string> {
  const response = await apiService.instance.get(`/xero/invoices/${encodeURIComponent(xeroInvoiceId)}/bill-pdf`, {
    responseType: 'blob',
  })
  const blob = response.data as Blob
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
  return URL.createObjectURL(pdfBlob)
}

/** Xero report types for Business Analytics (Syft) – uses Xero Reports API */
export type XeroReportType = 'ProfitAndLoss' | 'BalanceSheet' | 'ExecutiveSummary' | 'BankSummary'

export interface XeroReportResponse {
  report: any
  reportType: string
}

export async function getXeroReport(
  reportType: XeroReportType,
  params?: { fromDate?: string; toDate?: string; date?: string; periods?: string; timeframe?: string; standardLayout?: boolean }
): Promise<XeroReportResponse> {
  const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : ''
  const url = `/xero/reports/${reportType}${qs ? '?' + qs : ''}`
  const res = await apiService.get<{ success: boolean; data: XeroReportResponse }>(url, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Profitability: current vs previous period Net Profit from P&L (Xero-style) + margin data */
export interface XeroProfitabilityResponse {
  currentProfit: number
  previousProfit: number
  percentage: number
  currentLabel: string
  previousLabel: string
  previousShortLabel: string
  currentMonthAbbr: string
  previousMonthAbbr: string
  currentSalesRevenue: number
  previousSalesRevenue: number
  currentCogs: number
  previousCogs: number
}

export async function getXeroProfitability(
  fromDate: string,
  toDate: string,
  tenantId?: string
): Promise<XeroProfitabilityResponse> {
  const params = new URLSearchParams({ fromDate, toDate })
  if (tenantId) params.set('tenantId', tenantId)
  const res = await apiService.get<{ success: boolean; data: XeroProfitabilityResponse }>(`/xero/profitability?${params.toString()}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Snapshot metrics by date range (grouped revenue, cogs, expenses, margins per period) */
export interface SnapshotMetricRow {
  date: string
  revenue: number
  cogs: number
  expenses: number
  grossProfit: number
  netProfit: number
  grossMargin: number
  netMargin: number
}

export interface SnapshotMetricsResponse {
  metrics: SnapshotMetricRow[]
}

export async function getSnapshotMetrics(
  start: string,
  end: string,
  groupBy: 'day' | 'month',
  tenantId?: string
): Promise<SnapshotMetricsResponse> {
  const params = new URLSearchParams({ start, end, groupBy })
  if (tenantId) params.set('tenantId', tenantId)
  const res = await apiService.get<{ success: boolean; data: SnapshotMetricsResponse }>(`/xero/snapshot/metrics?${params.toString()}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** LOMS → Xero: Push unreconciled operational costs to Xero as bills */
export interface PushOperationalCostsResponse {
  pushed: number
  failed: number
  message: string
}

export async function pushXeroOperationalCosts(): Promise<PushOperationalCostsResponse> {
  const res = await apiService.post<{ success: boolean; data: PushOperationalCostsResponse }>('/xero/push/operational-costs')
  return (res as any)?.data ?? res
}

/** LOMS daily stock on hand and cost summary (for display or feeding to Xero) */
export interface StockAndCostSummaryResponse {
  asOf: string
  warehouses: { id: string; name: string; currentStock: number; location?: string }[]
  lots: { lotNumber: string; warehouseName?: string; tons?: number; bags?: number; balance?: number }[]
  totalOperationalCostByStage: { stage: string; total: number; currency: string }[]
  unreconciledCostCount: number
}

export async function getXeroStockSummary(): Promise<StockAndCostSummaryResponse> {
  const res = await apiService.get<{ success: boolean; data: StockAndCostSummaryResponse }>('/xero/stock-summary', xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Largest Expenses: Xero Business Snapshot style (current vs previous period) */
export interface LargestExpenseItem {
  name: string
  current: number
  previous: number
  change: 'up' | 'down' | 'same'
}

export interface LargestExpensesResponse {
  expenses: LargestExpenseItem[]
  currentLabel: string
  previousLabel: string
}

export async function getLargestExpenses(
  params: { filter?: string; fromDate?: string; toDate?: string; tenantId?: string }
): Promise<LargestExpensesResponse> {
  const qs = new URLSearchParams()
  if (params.filter) qs.set('filter', params.filter)
  if (params.fromDate) qs.set('fromDate', params.fromDate)
  if (params.toDate) qs.set('toDate', params.toDate)
  if (params.tenantId) qs.set('tenantId', params.tenantId)
  const res = await apiService.get<{ success: boolean; data: LargestExpensesResponse }>(`/xero/largest-expenses?${qs.toString()}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

export interface OperatingExpensesResponse {
  total: number
  items: { name: string; value: number }[]
  currentLabel: string
}

export async function getOperatingExpenses(
  params: { filter?: string; fromDate?: string; toDate?: string; tenantId?: string }
): Promise<OperatingExpensesResponse> {
  const qs = new URLSearchParams()
  if (params.filter) qs.set('filter', params.filter)
  if (params.fromDate) qs.set('fromDate', params.fromDate)
  if (params.toDate) qs.set('toDate', params.toDate)
  if (params.tenantId) qs.set('tenantId', params.tenantId)
  const res = await apiService.get<{ success: boolean; data: OperatingExpensesResponse }>(`/xero/operating-expenses?${qs.toString()}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Director's Remuneration: Xero Business Snapshot style */
export interface DirectorRemunerationResponse {
  name: string
  current: number
  previous: number
  change: 'up' | 'down' | 'same'
  currentLabel: string
  previousLabel: string
}

export async function getDirectorRemuneration(
  params: { filter?: string; fromDate?: string; toDate?: string; tenantId?: string }
): Promise<DirectorRemunerationResponse> {
  const qs = new URLSearchParams()
  if (params.filter) qs.set('filter', params.filter)
  if (params.fromDate) qs.set('fromDate', params.fromDate)
  if (params.toDate) qs.set('toDate', params.toDate)
  if (params.tenantId) qs.set('tenantId', params.tenantId)
  const res = await apiService.get<{ success: boolean; data: DirectorRemunerationResponse }>(`/xero/director-remuneration?${qs.toString()}`, xeroReadConfig)
  return (res as any)?.data ?? res
}

/** Financial position and cash: Balance sheet, cash balance, avg time to get paid / pay suppliers */
export interface FinancialPositionAndCashResponse {
  snapshotDate: string
  balanceSheet: { assets: number; liabilities: number; equity: number }
  cashBalance: {
    total: number
    accounts: { name: string; balance: number }[]
    totalAccounts: number
  }
  avgTimeToGetPaid: {
    days: number
    unpaidInvoicesValue: number
    previousPeriodDays: number | null
    previousPeriodLabel: string | null
  }
  avgTimeToPaySuppliers: {
    days: number
    unpaidBillsValue: number
    previousPeriodDays: number | null
    previousPeriodLabel: string | null
  }
}

export async function getFinancialPositionAndCash(
  toDate: string,
  fromDate: string | undefined,
  tenantId?: string
): Promise<FinancialPositionAndCashResponse> {
  const params = new URLSearchParams({ toDate })
  if (fromDate) params.set('fromDate', fromDate)
  if (tenantId) params.set('tenantId', tenantId)
  const res = await apiService.get<{ success: boolean; data: FinancialPositionAndCashResponse }>(
    `/xero/financial-position-cash?${params.toString()}`,
    xeroReadConfig
  )
  return (res as any)?.data ?? res
}

/** Financial ratios: 6 Xero formulas from Balance Sheet (snapshot at end of period, comparison = same date - 1 year) */
export interface FinancialRatioItem {
  current: number
  previous: number
  change: number
  trend: 'up' | 'down'
}

/** Underlying CA/CL used for Current ratio (same as Xero Balance Sheet). */
export interface CurrentRatioBreakdown {
  currentAssets: number
  currentLiabilities: number
}

/** Underlying figures for Working capital ÷ Total assets (Working capital = CA − CL). */
export interface WorkingCapitalBreakdown {
  workingCapital: number
  totalAssets: number
  currentAssets: number
  currentLiabilities: number
}

/** Underlying figures for Current liabilities ÷ Net worth. */
export interface NetWorthBreakdown {
  currentLiabilities: number
  netWorth: number
}

export interface RatioChartPoint {
  month: string
  current: number
  previous: number
  currentDate?: string
  previousDate?: string
  tooltipPrimaryLabel?: string
  tooltipPrimaryValue?: number
  tooltipSecondaryLabel?: string
  tooltipSecondaryValue?: number
}

export interface FinancialRatiosResponse {
  snapshotDate: string
  previousDate: string
  currentLabel: string
  previousLabel: string
  /** Underlying figures for Current ratio so UI can show CA ÷ CL and verify match with Xero. */
  currentRatioBreakdown?: {
    current: CurrentRatioBreakdown
    previous: CurrentRatioBreakdown
  }
  /** Underlying figures for Working capital ÷ Total assets (Working capital = CA − CL). */
  workingCapitalBreakdown?: {
    current: WorkingCapitalBreakdown
    previous: WorkingCapitalBreakdown
  }
  /** Underlying figures for Current liabilities ÷ Net worth. */
  currentLiabilitiesToNetWorthBreakdown?: {
    current: NetWorthBreakdown
    previous: NetWorthBreakdown
  }
  ratios: {
    currentRatio: FinancialRatioItem
    debtRatio: FinancialRatioItem
    currentLiabilitiesToNetWorth: FinancialRatioItem
    debtToEquity: FinancialRatioItem
    workingCapitalToTotalAssets: FinancialRatioItem
    fixedAssetsToNetWorth: FinancialRatioItem
  }
  chartData: {
    currentRatio: RatioChartPoint[]
    debtRatio: RatioChartPoint[]
    currentLiabilitiesToNetWorth: RatioChartPoint[]
    debtToEquity: RatioChartPoint[]
    workingCapitalToTotalAssets: RatioChartPoint[]
    fixedAssetsToNetWorth: RatioChartPoint[]
  }
}

export async function getFinancialRatios(
  fromDate: string,
  toDate: string,
  tenantId?: string
): Promise<FinancialRatiosResponse> {
  const params = new URLSearchParams({ fromDate, toDate })
  if (tenantId) params.set('tenantId', tenantId)
  const res = await apiService.get<{ success: boolean; data: FinancialRatiosResponse }>(
    `/xero/financial-ratios?${params.toString()}`,
    xeroReadConfig
  )
  return (res as any)?.data ?? res
}

/** Net profit margin: (Net Profit / Income) × 100 from P&L (accrual), current vs same period last year */
export interface NetProfitMarginResponse {
  currentMargin: number
  previousMargin: number
  marginChangePercent: number
  previousPeriodLabel: string
  labels: string[]
  currentSeries: number[]
  previousSeries: number[]
  explanation?: string
}

export async function getNetProfitMargin(
  from: string,
  to: string,
  params?: { filter?: string; tenantId?: string }
): Promise<NetProfitMarginResponse> {
  const qs = new URLSearchParams({ from, to })
  if (params?.filter) qs.set('filter', params.filter)
  if (params?.tenantId) qs.set('tenantId', params.tenantId)
  const res = await apiService.get<{ success: boolean; data: NetProfitMarginResponse }>(
    `/xero/net-profit-margin?${qs.toString()}`,
    xeroReadConfig
  )
  return (res as any)?.data ?? res
}

/** URL to open Xero (Analytics powered by Syft is inside Xero's app) */
export const XERO_ANALYTICS_URL = 'https://app.xero.com/'
